// src/pages/IpdReports.jsx
import React, { useEffect, useState, useMemo } from "react";
import axios from "axios";
import AppConst from "../apis/appconst";
import DataTable from "react-data-table-component";
import Swal from "sweetalert2";
import "bootstrap/dist/css/bootstrap.min.css";
import "bootstrap/dist/js/bootstrap.bundle.min.js";
// add these imports near top of file
import Select from "react-select";

import { jsPDF } from "jspdf";
import autoTable from "jspdf-autotable";

// ---------- Helpers ----------
const pad = (n) => String(n).padStart(2, "0");
const getTodayYMD = () => {
  const d = new Date();
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
};
const getNowHHMM = () => {
  const d = new Date();
  return `${pad(d.getHours())}:${pad(d.getMinutes())}`;
};

// display "HH:MM" (24) as "hh:mm AM/PM"
function display12From24(hhmm) {
  if (!hhmm) return "";
  const m = String(hhmm).match(/^(\d{1,2}):(\d{2})$/);
  if (!m) return hhmm;
  let h = parseInt(m[1], 10);
  const mm = m[2].padStart(2, "0");
  const ampm = h >= 12 ? "PM" : "AM";
  h = h % 12 || 12;
  return `${String(h).padStart(2, "0")}:${mm} ${ampm}`;
}

// parse flexible user input to 24-hour "HH:MM" or null if invalid
function parseTo24(input) {
  if (!input) return null;
  const s = String(input).trim().toLowerCase().replace(/\s+/g, ' ');
  // common patterns:
  // 3:30 pm, 03:30pm, 1530, 15:30, 3pm, 3 pm
  // try hh:mm with optional am/pm
  let m = s.match(/^(\d{1,2}):?(\d{2})?\s*(am|pm)?$/);
  if (m) {
    let h = parseInt(m[1], 10);
    let mm = m[2] ? m[2] : '00';
    const mer = m[3]; // am or pm or undefined
    if (mm.length === 1) mm = '0' + mm;
    if (mm.length !== 2 || parseInt(mm,10) > 59) return null;
    if (mer) {
      if (mer === 'pm' && h < 12) h += 12;
      if (mer === 'am' && h === 12) h = 0;
    }
    // if no am/pm and hour in 0-23 it's fine; but if hour <=12 ambiguous — we'll keep as-is (assume 24h if >12)
    if (!mer && h >=0 && h <= 23) {
      // ok
    }
    if (h < 0 || h > 23) return null;
    return `${String(h).padStart(2,'0')}:${String(mm).padStart(2,'0')}`;
  }
  return null;
}



// Convert "HH:MM" or "HH:MM:SS" (24-hour) to "hh:mm AM/PM"
function formatTime12(time24) {
  if (!time24) return "";
  // accept Date object or "HH:MM" or "HH:MM:SS"
  if (time24 instanceof Date) {
    let h = time24.getHours();
    const m = pad(time24.getMinutes());
    const ampm = h >= 12 ? "PM" : "AM";
    h = h % 12 || 12;
    return `${pad(h)}:${m} ${ampm}`;
  }
  const t = String(time24).trim();
  const m = t.match(/^(\d{1,2}):(\d{2})(?::\d{2})?$/);
  if (!m) return t;
  let hh = Number(m[1]);
  const mm = pad(Number(m[2]));
  const ampm = hh >= 12 ? "PM" : "AM";
  hh = hh % 12 || 12;
  return `${pad(hh)}:${mm} ${ampm}`;
}



// Parse server date strings like "YYYY-MM-DD", "YYYY-MM-DD HH:MM:SS" or ISO into a local Date
function parseServerDateToLocal(s) {
  if (!s) return null;
  if (s instanceof Date) return s;
  const normalized = String(s).trim().replace(" ", "T");
  const m = normalized.match(
    /^(\d{4})-(\d{2})-(\d{2})(?:[T ](\d{2}):(\d{2})(?::(\d{2}))?)?/
  );
  if (!m) {
    const fallback = new Date(s);
    return isNaN(fallback) ? null : fallback;
  }
  const year = Number(m[1]),
    month = Number(m[2]) - 1,
    day = Number(m[3]);
  const hour = Number(m[4] ?? 0),
    minute = Number(m[5] ?? 0),
    second = Number(m[6] ?? 0);
  return new Date(year, month, day, hour, minute, second);
}

function formatDateTime12(s) {
  const dt = parseServerDateToLocal(s);
  if (!dt) return "";
  const day = pad(dt.getDate());
  const months = [
    "Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"
  ];
  const mon = months[dt.getMonth()];
  const year = dt.getFullYear();
  let hr = dt.getHours();
  const min = pad(dt.getMinutes());
  const ampm = hr >= 12 ? "PM" : "AM";
  hr = hr % 12 || 12;
  return `${day}-${mon}-${year} ${pad(hr)}:${min} ${ampm}`;
}

function classifyMode(raw = "") {
  const s = String(raw).toLowerCase();
  if (s.includes("cash")) return "Cash";
  if (s.includes("card") || s.includes("debit") || s.includes("credit") || s.includes("swipe")) return "Card";
  if (s.includes("upi") || s.includes("online") || s.includes("gateway") || s.includes("net")) return "Online";
  return raw || "";
}

// Try to extract advance amount from common field names
function extractAdvance(r) {
  return Number(r.ipd_advance ?? r.advance ?? r.advance_amt ?? r.advance_amount ?? 0);
}

// Try to extract paid amount (used elsewhere too)
function extractPaid(r) {
  return Number(r.ipd_bill_total_paid ?? r.ipd_paid ?? r.paid ?? 0);
}




// ---------- Component ----------
export default function IpdReports() {
  const [filters, setFilters] = useState({
    // default show admitted patients (status 0)
    from: getTodayYMD(),
    fromTime: "00:00",
    to: getTodayYMD(),
    toTime: getNowHHMM(),
    dischargeFrom: "",
    dischargeFromTime: "00:00",
    dischargeTo: "",
    dischargeToTime: "23:59",
    doctorId: "",
    doctorSearch: "", // for searchable doctor input
    userId: "",
    paymentType: "All",
    search: "",
    status: "0" // <-- important: 0 = admitted, 1 = discharged
  });

  const [activeTab, setActiveTab] = useState("admitted"); // 'admitted' | 'discharged'
  const [records, setRecords] = useState([]);
  const [docsFromServer, setDocsFromServer] = useState([]); // backend doctor list
  const [users, setUsers] = useState([]); // derived from records

  const [totals, setTotals] = useState({
    totalAmount: "0.00",
    totalRegCharges: "0.00",
    totalAdvance: "0.00",
    totalIpdBill: "0.00",
    totalDiscount: "0.00",
    paidCash: "0.00",
    paidCard: "0.00",
    paidOnline: "0.00",
    totalDue: "0.00",
    totalPatients: 0
  });

// Improved export with robust error handling and server-message extraction
// inside IpdReports component

const handleExportPdf = async () => {
  try {
    // choose source rows: filteredRecords (client-side) OR fetch full server list if you prefer
    const rows = filteredRecords; // already defined via useMemo

    if (!rows || rows.length === 0) {
      Swal.fire('Nothing to export', 'No records available to export.', 'info');
      return;
    }

    const doc = new jsPDF('landscape', 'pt', 'a4');
    const title = `IPD Discharged Report`;
    const subtitle = `Date: ${getTodayYMD()}   Doctor: ${filters.doctorSearch || 'All'}   Filter: ${filters.paymentType || 'All'}`;

    // header
    doc.setFontSize(14);
    doc.text(title, 40, 40);
    doc.setFontSize(10);
    doc.text(subtitle, 40, 60);

    // summary row area (optional)
    const summaryY = 78;
    doc.setFontSize(9);
    doc.text(
      `Total Patients: ${totals.totalPatients}   ` +
      `Total IPD Bill: ₹${totals.totalIpdBill}   ` +
      `Paid (Cash): ₹${totals.paidCash}   ` +
      `Paid (Online): ₹${totals.paidOnline}   ` +
      `Paid (Card): ₹${totals.paidCard}`,
      40,
      summaryY
    );
    

    // Build autoTable columns and rows
    const head = [
      ['SL','IPD No','UHID','Patient Name','Discharge Date','IPD Bill','Cash','Card','Online','Due']
    ];

    const body = rows.map((r, idx) => {
      const ipdNo = `000${r.ipd_id ?? r.id ?? ''}`;
      const uhid = (r.ipd_remarks === "Moved from OPD" ? r.opd_uhid_move : r.ipd_uhid) || '';
      const name = (r.ipd_patient_name || '').replace(/\s+/g,' ');
      const discharge = formatDateTime12(r.ipd_discharge_date || r.discharge_datetime || r.discharge_date || '');
      const ipdBill = Number(r.ipd_bill_total_final ?? r.ipd_ipd_bill_amount ?? r.ipd_bill_amount ?? 0).toFixed(2);
      const cash = Number(r.ipd_paid_cash ?? 0).toFixed(2);
      const card = Number(r.ipd_paid_card ?? 0).toFixed(2);
      const online = Number(r.ipd_paid_online ?? 0).toFixed(2);
      const due = Number(r.ipd_bill_total_due ?? r.ipd_due ?? r.due ?? 0).toFixed(2);
      //const docName = getDoctorNameForRow(r) || '';

      return [
        String(idx + 1),
        ipdNo,
        uhid,
        name,
        discharge,
        ipdBill,
        cash,
        card,
        online,
        due,
        //docName
      ];
    });

    // Use autoTable to render the table below header
    autoTable(doc, {
      startY: 95,
      head: head,
      body: body,
      styles: { fontSize: 9, cellPadding: 4 },
      headStyles: { fillColor: [241,245,251], textColor: 60, halign: 'center' },
      columnStyles: {
        0: { cellWidth: 30 },   // SL
        1: { cellWidth: 60 },   // IPD No
        2: { cellWidth: 80 },   // UHID
        3: { cellWidth: 170 },  // Name
        4: { cellWidth: 90 },   // Discharge Date
        5: { cellWidth: 70, halign: 'right' }, // IPD Bill
        6: { cellWidth: 60, halign: 'right' }, // Cash
        7: { cellWidth: 60, halign: 'right' }, // Card
        8: { cellWidth: 60, halign: 'right' }, // Online
        9: { cellWidth: 60, halign: 'right' }, // Due
        10: { cellWidth: 120 } // Doctor
      },
      didDrawPage: (data) => {
        // optional: page footer with page number
        const pageCount = doc.getNumberOfPages();
        const page = doc.getCurrentPageInfo().pageNumber;
        doc.setFontSize(9);
        doc.text(`Page ${page} of ${pageCount}`, doc.internal.pageSize.width - 60, doc.internal.pageSize.height - 10, { align: 'right' });
      },
      // if very wide, will auto pageBreak
      margin: { left: 40, right: 20, top: 40 }
    });

    // Save file
    doc.save(`ipd_discharged_${getTodayYMD()}.pdf`);
  } catch (err) {
    console.error('PDF export failed', err);
    Swal.fire('Export failed', 'Could not generate PDF in browser. See console.', 'error');
  }
};





  // ---------- Fetching ----------
  const loadRecords = async () => {
    try {
      const fd = new FormData();
      fd.append("page_no", "none");

      // Decide which backend to call:
      // - discharged tab -> call dedicated discharged endpoint and send discharge filters
      // - admitted (or default) -> call existing endpoint and send admission filters
      const isDischarged = String(filters.status) === "1";

      if (!isDischarged) {
        // admission date/time filters (existing behavior)
        if (filters.from) fd.append("from", filters.from);
        if (filters.fromTime) fd.append("fromTime", filters.fromTime);
        if (filters.to) fd.append("to", filters.to);
        if (filters.toTime) fd.append("toTime", filters.toTime);
      } else {
        // discharged endpoint: send dischargeFrom/dischargeTo + times
        if (filters.dischargeFrom) fd.append("dischargeFrom", filters.dischargeFrom);
        if (filters.dischargeFromTime) fd.append("dischargeFromTime", filters.dischargeFromTime);
        if (filters.dischargeTo) fd.append("dischargeTo", filters.dischargeTo);
        if (filters.dischargeToTime) fd.append("dischargeToTime", filters.dischargeToTime);
      }

      // always send status so backend knows context (even though discharged endpoint forces status=1)
      if (typeof filters.status !== "undefined" && filters.status !== "") {
        fd.append("status", String(filters.status)); // '0' or '1'
      }

      if (filters.userId) fd.append("ipd_user", filters.userId);
      if (filters.search) fd.append("search_key", filters.search);

      if (filters.paymentType && filters.paymentType !== "All") {
        fd.append("ipd_bill_pay_mode", filters.paymentType);
      }

      // doctorId present when user selected a doctor from datalist exact match
      if (filters.doctorId) {
        fd.append("doctor_id", filters.doctorId);
      } else if (filters.doctorSearch) {
        // optional: if you want server to accept a free-text doctor search
        // fd.append('doctor_search', filters.doctorSearch);
      }

      // choose url based on tab
      const url = isDischarged && AppConst.getIpdDischargedUrl
        ? (AppConst.BASE_URL + AppConst.getIpdDischargedUrl)
        : (AppConst.BASE_URL + AppConst.getIpdListUrl2);

      const res = await axios.post(url, fd);

      if (res?.data?.status && Array.isArray(res.data.records)) {
        setRecords(res.data.records);
      
        // set server totals if provided
        const t = res.data.totals || {};
        setTotals(prev => ({
          ...prev,
          totalIpdBill: (t.sum_ipd_bill ?? t.sum_ipd_bill ?? 0).toFixed(2),
          paidCash: (t.total_cash ?? t.paidCash ?? 0).toFixed(2),
          paidCard: (t.total_card ?? t.paidCard ?? 0).toFixed(2),
          paidOnline: (t.total_online ?? t.paidOnline ?? 0).toFixed(2),
          totalDue: (t.sum_due ?? t.total_due ?? 0).toFixed(2),
          totalPatients: (t.count ?? (res.data.records || []).length)
        }));
      } else {
        setRecords([]);
        // clear totals if needed
        setTotals(prev => ({ ...prev, totalIpdBill: "0.00", paidCash:"0.00", paidCard:"0.00", paidOnline:"0.00", totalDue:"0.00", totalPatients:0 }));
      }
      
    } catch (e) {
      console.error("loadRecords error:", e);
      setRecords([]);
    }
  };

// ---------- Pay Mode Renderer ----------
function renderPayModes(record) {
  const raw = record.ipd_bill_pay_modes || '';
  const parts = raw.split(',').map(p => p.trim()).filter(Boolean);

  const mapToLabel = (m) => {
    if (!m) return '';
    m = m.toLowerCase();
    if (m.includes('upi') || m.includes('netbank')) return 'online';
    if (m.includes('card')) return 'card';
    if (m.includes('cash')) return 'cash';
    return m;
  };

  const normalized = Array.from(new Set(parts.map(mapToLabel)));

  // Preferred order: Card → Online → Cash
  const order = ['card','online','cash'];
  normalized.sort((a,b) => order.indexOf(a) - order.indexOf(b));

  return normalized.map((m,i) => {
    const label = m.charAt(0).toUpperCase() + m.slice(1);
    const icon = m === 'cash' ? '💵' : (m === 'card' ? '💳' : '🌐');
    return (
      <span key={i} className="badge badge-sm mr-1" style={{background:'#e9ecef', color:'#333'}}>
        {icon} {label}
      </span>
    );
  });
}

  const fetchDoctors = async () => {
    try {
      const fd = new FormData();
      fd.append("type", "doctor");
      fd.append("page_no", "none");
      const res = await axios.post(AppConst.BASE_URL + AppConst.getUserListUrl, fd);
      if (res?.data?.status && Array.isArray(res.data.records)) {
        setDocsFromServer(res.data.records);
      }
    } catch (e) {
      console.error("fetchDoctors error:", e);
    }
  };

  // initial load
  useEffect(() => {
    loadRecords();
    fetchDoctors();
    // eslint-disable-next-line
  }, []);

  // auto-refresh when filters change (debounced)
  useEffect(() => {
    const t = setTimeout(() => loadRecords(), 220);
    return () => clearTimeout(t);
    // eslint-disable-next-line
  }, [filters]);

  // derive users from records (for the User dropdown)
  useEffect(() => {
    const uMap = {};
    records.forEach(r => {
      const uid = r.last_used_id ?? r.user_id ?? r.ipd_user ?? null;
      const uname = (r.last_used && String(r.last_used).trim()) || (r.fname && String(r.fname).trim()) || (r.user_display && String(r.user_display).trim()) || (uid ? `User#${uid}` : '');
      if (uid) uMap[String(uid)] = uname;
    });
    setUsers(Object.entries(uMap).map(([k, v]) => ({ user_id: k, name: v })));
  }, [records]);

  // ---------- Doctor name resolver ----------
 // Helper: resolve doctor id -> friendly name (optional row r can provide server-friendly names)
const getDoctorNameById = (id, r = null) => {
  if (!id && !r) return "";

  // If the server already sent a friendly name on the row, prefer it.
  // Check common friendly-name fields (referred/consultant/etc.)
  if (r) {
    const friendlyKeys = [
      'ipd_refer_doctor_name',
      'ipd_bill_refered_doctor_name',
      'ipd_refer_doctor_name',
      'ipd_ref_doc_name',
      'ipd_cons_doc_name',
      'consultant_name',
      'doctor_name'
    ];
    for (const k of friendlyKeys) {
      if (r[k]) return String(r[k]);
    }
  }

  // If id is empty after checking row, return ''
  if (!id && !r) return "";

  // Normalize candidate id (handle numeric strings, comma-lists like "12,34")
  let candidate = id;
  if (typeof candidate === 'string' && candidate.indexOf(',') !== -1) {
    candidate = candidate.split(',')[0].trim();
  }

  // Lookup in docsFromServer (client-side doctor list)
  const doc = docsFromServer.find(d => String(d.user_id) === String(candidate) || String(d.id) === String(candidate));
  if (doc) return doc.fname || doc.name || doc.username || String(candidate);

  // Fallback: if row had a name-like field other than friendly keys, try a few more possibilities
  if (r) {
    const altKeys = ['ref_doc_name','refer_doctor_name','doctor','ref_doc','ref_doc_id'];
    for (const k of altKeys) {
      if (r[k]) return String(r[k]);
    }
  }

  // Last resort: return id string (or empty)
  return candidate ? String(candidate) : '';
};

// Referred / normal doctor resolver (use getDoctorNameById under the hood)
const getDoctorNameForRow = (row) => {
  if (!row) return "";
  // prefer any server-provided friendly field first
  const friendly = row.ipd_bill_refered_doctor_name || row.ipd_refer_doctor_name || row.ipd_ref_doc_name || row.ipd_cons_doc_name || row.consultant_name;
  if (friendly) return String(friendly);

  // then prefer id fields
  const id = row.ipd_bill_refered_doctor ?? row.ipd_refer_doctor ?? row.ipd_ref_doc ?? row.ipd_cons_doc ?? row.refer_doctor ?? null;
  if (id) return getDoctorNameById(id, row);

  return "";
};

// Robust OT doctor resolver — re-uses getDoctorNameById
const getOtDoctorNameForRow = (row) => {
  if (!row) return "";

  // prefer explicit server-sent text fields (OT/surgeon/anesthesia)
  const textKeys = [
    'ot_doctor',
    'ot_doctor_name',
    'ipd_ot_doctor_name',
    'ipd_ot_doctor',
    'surgeon_name',
    'surgeon',
    'ipd_anesthesia_doctor',
    'ipd_anesthesia_doctor_name',
    'anesthesia_doctor'
  ];
  for (const k of textKeys) {
    if (row[k]) return String(row[k]);
  }

  // fallback to id-like keys and resolve to name via docsFromServer
  const idKeys = [
    'ot_doctor_id',
    'ipd_ot_doctor',
    'surgeon_id',
    'ipd_surgeon_id',
    'ipd_anesthesia_doctor_id',
    'ipd_surgeon'
  ];
  for (const k of idKeys) {
    if (typeof row[k] !== 'undefined' && row[k] !== null && row[k] !== '') {
      return getDoctorNameById(row[k], row);
    }
  }

  return "";
};







  // ---------- Filtering (doctor/payment/user/search + status) ----------
  const filteredRecords = useMemo(() => {
    let data = [...records];

    // Server should already filter by status, but we also enforce it on client side for safety:
   // Only enforce `status` filter for the Discharged tab.
// For Admitted tab we want to show admitted patients regardless of r.status (0 or 1).
if (activeTab === 'discharged' && filters.status !== "" && typeof filters.status !== "undefined") {
  data = data.filter(r => String(r.status) === String(filters.status));
}


    // Doctor filter: match either id or name
    if (filters.doctorId && filters.doctorId !== "") {
      const sel = String(filters.doctorId).toLowerCase();
      data = data.filter(r => {
        const idMatch = String(r.ipd_bill_refered_doctor ?? r.ipd_refer_doctor ?? r.ipd_ref_doc ?? r.ipd_cons_doc ?? '').toLowerCase() === sel;
        const nameMatch = (getDoctorNameForRow(r) || '').toLowerCase().includes(sel);
        return idMatch || nameMatch;
      });
    }

    // Payment type filter (frontend)
    if (filters.paymentType && filters.paymentType !== "All") {
      const type = filters.paymentType.toLowerCase();
      data = data.filter(r => {
        if (type === 'cash') return (Number(r.ipd_paid_cash ?? 0) > 0) || (String(r.ipd_bill_pay_modes || '').toLowerCase().includes('cash'));
        if (type === 'card') return (Number(r.ipd_paid_card ?? 0) > 0) || (String(r.ipd_bill_pay_modes || '').toLowerCase().includes('card'));
        if (type === 'online') return (Number(r.ipd_paid_online ?? 0) > 0) || (String(r.ipd_bill_pay_modes || '').toLowerCase().includes('upi') || String(r.ipd_bill_pay_modes || '').toLowerCase().includes('online') || String(r.ipd_bill_pay_modes || '').toLowerCase().includes('netbank'));
        return true;
      });
    }
    

    // User filter
    if (filters.userId && filters.userId !== "") {
      data = data.filter(r => {
        const uid = r.last_used_id ?? r.user_id ?? r.ipd_user ?? '';
        return String(uid) === String(filters.userId);
      });
    }

    // Search (UHID / name / mobile) - client side
    // Search (Name | UHID | IPD No | Phone) - client side (improved)
if (filters.search && filters.search.trim() !== "") {
  const skRaw = filters.search.trim();
  const sk = skRaw.toLowerCase();

  // support searching by number-only inputs too (e.g., IPD no or phone)
  const digitsOnly = skRaw.replace(/\D/g, '');

  data = data.filter(r => {
    // UHID might be in ipd_uhid or opd_uhid_move (moved patients)
    const uhid = ((r.ipd_remarks === "Moved from OPD" ? r.opd_uhid_move : r.ipd_uhid) || '').toString().toLowerCase();

    // name
    const name = (r.ipd_patient_name || '').toString().toLowerCase();

    // various phone fields commonly used
    const phones = [
      r.ipd_patient_mobile,
      r.ipd_cont_number,
      r.ipd_patient_phone,
      r.patient_mobile,
      r.mobile,
      r.phone
    ].filter(Boolean).map(p => String(p).toLowerCase());

    // IPD id (with leading zeros shown in UI)
    const ipdNoDisplayed = `000${r.ipd_id ?? r.id ?? ''}`.toLowerCase();

    // matchers
    if (name.includes(sk)) return true;
    if (uhid.includes(sk)) return true;
    if (ipdNoDisplayed.includes(sk)) return true;

    // match on any phone field (allow partials and digit-only searches)
    for (const ph of phones) {
      if (ph.includes(sk)) return true;
      // if user typed digits only, also match against phone digits-only
      if (digitsOnly && ph.replace(/\D/g, '').includes(digitsOnly)) return true;
    }

    // also allow searching by raw numeric IPD id (e.g., user types '123' to match ipd_id)
    if (digitsOnly) {
      const ipdIdStr = String(r.ipd_id ?? r.id ?? '').replace(/\D/g, '');
      if (ipdIdStr.includes(digitsOnly)) return true;
    }

    return false;
  });
}


    // Discharge date/time filters (if user set)
    if (filters.dischargeFrom || filters.dischargeTo) {
      const fromDt = filters.dischargeFrom ? new Date(`${filters.dischargeFrom}T${filters.dischargeFromTime || '00:00'}`) : null;
      const toDt = filters.dischargeTo ? new Date(`${filters.dischargeTo}T${filters.dischargeToTime || '23:59'}`) : null;
      data = data.filter(r => {
        const ds = r.ipd_discharge_date || r.discharge_datetime || r.discharge_date || null;

        const dt = parseServerDateToLocal(ds);
        if (!dt) return false;
        if (fromDt && dt < fromDt) return false;
        if (toDt && dt > toDt) return false;
        return true;
      });
    }

    // Admission date/time window filter (from & to + times)
    if (filters.from || filters.to) {
      const fromDt = filters.from ? new Date(`${filters.from}T${filters.fromTime || '00:00'}`) : null;
      const toDt = filters.to ? new Date(`${filters.to}T${filters.toTime || '23:59'}`) : null;
      data = data.filter(r => {
        const as = r.ipd_reg_date || r.ipd_datetime || null;
        const dt = parseServerDateToLocal(as);
        if (!dt) return false;
        if (fromDt && dt < fromDt) return false;
        if (toDt && dt > toDt) return false;
        return true;
      });
    }

    return data;
  }, [records, filters, docsFromServer]);

  // ---------- Totals computed from filtered list ----------
// ---------- Totals computed from filtered list (per-method sums) ----------
// ---------- Totals computed from filtered list (per-method sums) ----------
useEffect(() => {
  // small helper to coerce numbers
  const num = (v) => {
    const n = Number(v ?? 0);
    return isNaN(n) ? 0 : n;
  };

  // helper: parse a payment-mode string (e.g. "Cash", "card", "UPI", "cash,card", "cash:100,card:50")
  // returns array of {method: 'cash'|'card'|'online'|'other', amount: number|null}
  const parseModeString = (s) => {
    if (!s) return [];
    // normalize separators and trim
    const parts = String(s).split(/[,\|;]/).map(p => p.trim()).filter(Boolean);
    if (parts.length === 0) return [];

    // If parts contain explicit amounts like "cash:100" or "cash=100" extract them
    const out = parts.map(p => {
      const m = p.match(/([a-zA-Z_\-\s]+)[\s:=]*([\d.]+)/);
      if (m) {
        const method = m[1].toLowerCase().trim();
        const amount = num(m[2]);
        if (method.includes('cash')) return { method: 'cash', amount };
        if (method.includes('card') || method.includes('debit') || method.includes('credit') || method.includes('swipe')) return { method: 'card', amount };
        if (method.includes('upi') || method.includes('online') || method.includes('net')) return { method: 'online', amount };
        return { method: method, amount };
      }
      // no explicit amount — detect method by keyword
      const kw = p.toLowerCase();
      if (kw.includes('cash')) return { method: 'cash', amount: null };
      if (kw.includes('card') || kw.includes('debit') || kw.includes('credit') || kw.includes('swipe')) return { method: 'card', amount: null };
      if (kw.includes('upi') || kw.includes('online') || kw.includes('net')) return { method: 'online', amount: null };
      return { method: kw, amount: null };
    });

    return out;
  };

  // Totals
  let totalReg = 0, totalAdvance = 0, totalIpd = 0;
  let paidCash = 0, paidCard = 0, paidOnline = 0;
  let totalDue = 0;
  let totalDiscount = 0;

  if (activeTab === 'admitted') {
    // For admitted tab: use explicit fields requested by user:
    // ipd_total_paid, ipd_reg_charges, ipd_advance, ipd_discount
    filteredRecords.forEach(r => {
      const reg = num(r.ipd_reg_charges ?? r.ipd_reg_charge ?? 0);
      const advance = num(r.ipd_advance ?? r.advance ?? 0);
      const paid = num(r.ipd_total_paid ?? r.ipd_bill_total_paid ?? r.ipd_paid ?? 0);
      const discount = num(r.ipd_discount ?? r.discount ?? 0);
      const due  = num(r.ipd_due ?? r.due ?? 0);
  
      totalReg += reg;
      totalAdvance += advance;
      totalIpd += paid;       // total amount for admitted -> ipd_total_paid
      totalDue += due;
      totalDiscount += discount;
  
      // For per-method paid breakdown try to use explicit per-method fields if present,
      // otherwise fall back to parsing pay-mode string and distributing `paid`.
      // Prefer explicit fields (if backend provided them).
      const explicitCash = num(r.ipd_paid_cash ?? r.paid_cash ?? r.paidCash ?? 0);
      const explicitCard = num(r.ipd_paid_card ?? r.paid_card ?? r.paidCard ?? 0);
      const explicitOnline = num(r.ipd_paid_online ?? r.paid_online ?? r.paidOnline ?? 0);
  
      if (explicitCash || explicitCard || explicitOnline) {
        paidCash += explicitCash;
        paidCard += explicitCard;
        paidOnline += explicitOnline;
      } else {
        // fall back to parsing mode string and distributing `paid`
        const modeStr = (r.ipd_pay_mode ?? r.ipd_bill_pay_modes ?? '').toString();
        const parts = parseModeString(modeStr);
        if (!parts || parts.length === 0) {
          // no mode info — guess from keywords
          const ms = modeStr.toLowerCase();
          if (ms.includes('cash')) { paidCash += paid; }
          else if (ms.includes('card')) { paidCard += paid; }
          else if (ms.includes('upi') || ms.includes('online') || ms.includes('net')) { paidOnline += paid; }
          else {
            // unknown — keep as cash fallback (you can change this policy)
            paidCash += paid;
          }
        } else {
          // distribute `paid` among detected methods (or use explicit amounts inside parts if present)
          let explicitSum = 0, explicitPresent = false;
          parts.forEach(p => { if (p.amount !== null) { explicitPresent = true; explicitSum += num(p.amount); } });
  
          if (explicitPresent) {
            parts.forEach(p => {
              const mth = p.method;
              const amt = num(p.amount);
              if (mth === 'cash') paidCash += amt;
              else if (mth === 'card') paidCard += amt;
              else if (mth === 'online') paidOnline += amt;
              else paidCash += amt;
            });
          } else {
            // split equally among detected methods
            const methods = Array.from(new Set(parts.map(p => p.method)));
            const recognized = methods.filter(m => ['cash','card','online'].includes(m));
            const toSplit = recognized.length ? recognized : methods;
            const share = toSplit.length ? (paid / toSplit.length) : 0;
            toSplit.forEach(m => {
              if (m === 'cash') paidCash += share;
              else if (m === 'card') paidCard += share;
              else if (m === 'online') paidOnline += share;
              else paidCash += share;
            });
          }
        }
      }
    });
  }
   else {
    // For non-admitted (discharged) keep original behavior: use backend-provided per-method fields if present
    filteredRecords.forEach(r => {
      const reg = num(r.ipd_reg_charge ?? r.ipd_reg_charges ?? 0);
      const advance = num(r.ipd_advance ?? r.advance ?? r.advance_amt ?? 0);
      const ipdBill = num(r.ipd_bill_total_final ?? r.ipd_ipd_bill_amount ?? r.ipd_bill_amount ?? r.ipd_final_amt ?? 0);
      const due  = num(r.ipd_bill_total_due ?? r.ipd_due ?? r.due ?? 0);

      totalReg += reg;
      totalAdvance += advance;
      totalIpd += ipdBill;
      totalDue += due;

      // prefer explicit per-method fields populated by backend
      paidCash += num(r.ipd_paid_cash ?? r.paid_cash ?? r.paidCash ?? 0);
      paidCard += num(r.ipd_paid_card ?? r.paid_card ?? r.paidCard ?? 0);
      paidOnline += num(r.ipd_paid_online ?? r.paid_online ?? r.paidOnline ?? 0);
    });
  }

  const totalAmountCalc = totalReg + totalAdvance; // kept for admitted tab

  setTotals(prev => ({
    ...prev,
    totalAmount: totalAmountCalc.toFixed(2),
    totalRegCharges: totalReg.toFixed(2),
    totalAdvance: totalAdvance.toFixed(2),
    totalIpdBill: totalIpd.toFixed(2),
    totalDiscount: totalDiscount.toFixed(2),
    paidCash: paidCash.toFixed(2),
    paidCard: paidCard.toFixed(2),
    paidOnline: paidOnline.toFixed(2),
    totalDue: totalDue.toFixed(2),
    totalPatients: filteredRecords.length
  }));
  
}, [filteredRecords, activeTab]);



  // ---------- Handlers ----------
  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters(prev => ({ ...prev, [name]: value }));
  };

  // Doctor search typed/selected
  const handleDoctorSearchChange = (e) => {
    const val = e.target.value;
    // if user types an id||name combo we want to parse; otherwise just set doctorSearch
    setFilters(prev => ({ ...prev, doctorSearch: val }));
    // if exact match with a doctor name, set the doctorId as well
    const found = docsFromServer.find(d => (d.fname || d.name || d.username || "").toLowerCase() === val.toLowerCase());
    if (found) {
      setFilters(prev => ({ ...prev, doctorId: String(found.user_id), doctorSearch: val }));
    } else {
      // clear doctorId when search doesn't match exactly
      setFilters(prev => ({ ...prev, doctorId: "" }));
    }
  };

  // Tab click
  const todayYMD = getTodayYMD();

  const handleTabClick = (tab) => {
    const today = getTodayYMD();
    const nowHHMM = getNowHHMM();
  
    if (tab === "admitted") {
      setActiveTab("admitted");
      setFilters(prev => ({
        ...prev,
        status: "0",
        // set admission window to today: 00:00 -> now
        from: today,
        fromTime: "00:00",
        to: today,
       toTime: '23:59'
        // keep discharge filters as they are (no change)
      }));
    } else {
      // discharged
      setActiveTab("discharged");
      setFilters(prev => ({
        ...prev,
        status: "1",
        // set discharge window to today full-day only if they are empty
        dischargeFrom: prev.dischargeFrom || today,
        dischargeFromTime: prev.dischargeFromTime || '00:00',
        dischargeTo: prev.dischargeTo || today,
        dischargeToTime: prev.dischargeToTime || '23:59',
        // clear admission window to avoid confusion
        from: '',
        fromTime: '00:00',
        to: '',
        toTime: '23:59'
      }));
    }
  };
  
  

  const openPatientCard = async (row) => {
    if (!row) return;
  
    // html-escape helper
    const esc = (s) => {
      if (s === null || typeof s === 'undefined') return '';
      return String(s)
        .replace(/&/g,'&amp;')
        .replace(/</g,'&lt;')
        .replace(/>/g,'&gt;')
        .replace(/"/g,'&quot;')
        .replace(/'/g,'&#039;');
    };
  
    // patient summary
    const uhid = row.ipd_uhid || row.opd_uhid_move || row.opd_uhid || '';
    const wardName = row.ipd_ward_name || row.ward_name || row.ipd_ward || 'N/A';
    const bedName  = row.ipd_bed_name  || row.bed_name || row.ipd_bed || 'N/A';
    const referred = getDoctorNameForRow(row) || row.ipd_refer_doctor || '';
    const regDate = formatDateTime12(row.ipd_datetime);
    const discharge = formatDateTime12(row.discharge_datetime);
    const otDoctor = row.ot_doctor_name || getOtDoctorNameForRow(row) || row.ipd_ot_doctor || row.ipd_anesthesia_doctor || '';

    const patientHtml = `
      <div style="text-align:left">
        <p><strong>UHID:</strong> ${esc(uhid) || 'N/A'}</p>
        <p><strong>IPD No:</strong> ${esc(row.ipd_id ?? row.id ?? 'N/A')}</p>
        <p><strong>Name:</strong> ${esc(row.ipd_patient_name ?? 'N/A')}</p>
        <p><strong>Phone:</strong> ${esc(row.ipd_patient_mobile ?? row.ipd_cont_number ?? 'N/A')}</p>
        <p><strong>Ward:</strong> ${esc(wardName)} &nbsp; <strong>Bed:</strong> ${esc(bedName)}</p>
        <p><strong>Admission:</strong> ${esc(regDate)}</p>
        <p><strong>Discharge:</strong> ${esc(discharge)}</p>
        <p><strong>Referred Doctor:</strong> ${esc(referred || '-')}</p>
         <p><strong>Surgeon/OT Doctor:</strong> ${getDoctorNameById(row.ot_doctor)}</p>
        <p><strong>Reg Charges:</strong> ${Number(row.ipd_reg_charges ?? 0).toFixed(2)}</p>
        <p><strong>Advance:</strong> ${Number(row.ipd_advance ?? 0).toFixed(2)}</p>
        <p><strong>IPD Total Paid: (Reg. Charges + Advance) </strong> ${Number(row.ipd_total_paid ?? row.paid ?? row.ipd_bill_total_paid ?? 0).toFixed(2)} &nbsp; <strong>Due:</strong> ${Number(row.ipd_due ?? row.due ?? row.ipd_bill_total_due ?? 0).toFixed(2)}</p>
      <p><strong>IPD Bill Paid:</strong> ${Number(row.ipd_bill_paid ?? 0).toFixed(2)} &nbsp; <strong>IPD Bill Due:</strong> ${Number(row.ipd_bill_due ?? 0).toFixed(2)}</p>
        </div>
    `;
  
    // default items area while we fetch
    let itemsTableHtml = `<div class="mt-2"><strong>Services:</strong><div>No services found</div></div>`;
  
    try {
      // request bill items
      const fd = new FormData();
      if (row.ipd_id) fd.append('ipd_id', String(row.ipd_id));
      if (row.ipd_uhid) fd.append('ipd_uhid', String(row.ipd_uhid));
      if (row.ipd_no) fd.append('ipd_no', String(row.ipd_no));
  
      const itemsUrl = AppConst.BASE_URL + (AppConst.getIpdBillItemsUrl || '/api/get_ipd_bill_items');
  
      const res = await axios.post(itemsUrl, fd);
      console.debug('openPatientCard: items response', res?.data);
  
      // detect items array in common shapes
      let items = [];
      if (res?.data) {
        if (Array.isArray(res.data.items)) items = res.data.items;
        else if (Array.isArray(res.data.records)) items = res.data.records;
        else if (Array.isArray(res.data.data)) items = res.data.data;
        else if (Array.isArray(res.data)) items = res.data;
      }
  
      if (items && items.length > 0) {
        // build table header + rows using requested columns
        const rowsHtml = items.map(it => {
          const svc = esc(it.ipd_bill_item_service ?? it.service ?? it.name ?? it.item_name ?? 'Unknown service');
          // qty, charge, discount, final (fallbacks)
          const qty = Number(it.ipd_bill_item_qty ?? it.qty ?? it.quantity ?? 0);
          const charge = Number(it.ipd_bill_item_charge ?? it.charge ?? it.price ?? 0);
          const dis = Number(it.ipd_bill_item_dis ?? it.discount ?? it.ipd_bill_item_discount ?? 0);
          const finalAmt = Number(it.ipd_bill_item_final ?? it.final ?? it.ipd_bill_item_amount ?? 0);
  
          return `<tr>
            <td style="padding:6px 10px;border-bottom:1px solid #eee">${svc}</td>
            <td style="padding:6px 10px;border-bottom:1px solid #eee;text-align:center">${qty}</td>
            <td style="padding:6px 10px;border-bottom:1px solid #eee;text-align:right">${charge.toFixed(2)}</td>
            <td style="padding:6px 10px;border-bottom:1px solid #eee;text-align:right">${dis.toFixed(2)}</td>
            <td style="padding:6px 10px;border-bottom:1px solid #eee;text-align:right">${finalAmt.toFixed(2)}</td>
          </tr>`;
        }).join('');
  
        itemsTableHtml = `
          <div class="mt-2" style="width:100%;overflow:auto">
            <strong>Services:</strong>
            <table style="width:100%;border-collapse:collapse;margin-top:6px">
              <thead>
                <tr style="background:#f7f9fc;">
                  <th style="text-align:left;padding:8px 10px;border-bottom:1px solid #ddd">Service</th>
                  <th style="text-align:center;padding:8px 10px;border-bottom:1px solid #ddd;width:80px">Qty</th>
                  <th style="text-align:right;padding:8px 10px;border-bottom:1px solid #ddd;width:110px">Charge</th>
                  <th style="text-align:right;padding:8px 10px;border-bottom:1px solid #ddd;width:100px">Disc</th>
                  <th style="text-align:right;padding:8px 10px;border-bottom:1px solid #ddd;width:110px">Final</th>
                </tr>
              </thead>
              <tbody>
                ${rowsHtml}
              </tbody>
            </table>
          </div>
        `;
      } else {
        itemsTableHtml = `<div class="mt-2"><strong>Services:</strong><div>No services found</div></div>`;
        console.debug('openPatientCard: items empty or not found shape', res?.data);
      }
    } catch (err) {
      console.warn('openPatientCard: failed to fetch bill items', err);
      itemsTableHtml = `<div class="mt-2"><strong>Services:</strong><div class="text-danger">Could not load services</div></div>`;
    }
  
    const finalHtml = `<div>${patientHtml}${itemsTableHtml}</div>`;
  
    Swal.fire({
      title: `Patient: ${esc(row.ipd_patient_name ?? '')}`,
      html: finalHtml,
      width: 900,
      showCloseButton: true,
      confirmButtonText: 'Close'
    });
  };
  
  const viewButtonColumn = {
    name: 'View',
    width: '100px',
    cell: (r) => (
      <button
        className="btn btn-sm btn-outline-primary"
        onClick={(e) => {
          e.stopPropagation(); // prevent row click firing duplicate handlers
          openPatientCard(r);
        }}
      >
        View
      </button>
    )
  };
  
  // ---------- Columns (dynamic based on activeTab) ----------
  const commonColumns = [
    { name: 'SL', selector: (r,i) => i+1, width: "60px" },
    { name: 'IPD No', selector: r => `000${r.ipd_id ?? r.id ?? ''}`, sortable: true, width: "140px" },
    { name: 'UHID', selector: r => (r.ipd_remarks === "Moved from OPD" ? r.opd_uhid_move : r.ipd_uhid) || '', sortable: true, width: "150px" },
    { name: 'Patient Name', selector: r => `${r.ipd_patient_title || ''} ${r.ipd_patient_name || ''}`, cell: r => (<div>{r.ipd_patient_name}<br/><small className="text-muted">{r.ipd_patient_mobile || r.ipd_cont_number}</small></div>), wrap: true, width: "220px" },
    { name: 'Bed', selector: r => r.ipd_bed_name || r.ipd_bed || '', sortable: true, width: "140px" },
    { name: 'Referred Doctor', selector: r => getDoctorNameForRow(r), sortable: true, width: "200px" },
    {
      name: 'OT Doctor',
      selector: r => getDoctorNameById(r.ot_doctor),
      width: "200px"
    },
  ];

  // admitted tab columns
  const admittedColumns = [
    ...commonColumns.slice(0,3), // SL, IPD No, UHID (keeping order)
    { name: 'Admission Date', selector: r => r.ipd_reg_date || r.ipd_datetime || '', sortable: true, cell: r => formatDateTime12(r.ipd_reg_date || r.ipd_datetime), width: "200px" },
    ...commonColumns.slice(3), // Patient Name, Bed, Referred Doctor, OT Doctor
    { name: 'Reg Charges', selector: r => Number(r.ipd_reg_charge ?? r.ipd_reg_charges ?? 0).toFixed(2), right: true, width: "120px" },
    { name: 'Advance', selector: r => extractAdvance(r).toFixed(2), right: true, width: "120px" },
    {
      name: 'Paid',
      selector: r => Number(r.ipd_bill_total_paid ?? r.ipd_paid ?? r.paid ?? 0).toFixed(2),
      right: true,
      width: "110px"
    },
    { name: 'Due',  selector: r => Number(r.ipd_bill_total_due  ?? r.ipd_due  ?? r.due ?? 0).toFixed(2), right: true, width: "110px" },
    {
      name: 'Payment Type',
      selector: r => classifyMode(r.ipd_bill_pay_modes || r.ipd_bill_pay_mode || r.ipd_pay_mode || ''),
      width: "120px"
    },
    // {
    //   name: 'Status',
    //   selector: r => r.status,
    //   width: "130px",
    //   cell: r => (
    //     r.status == 1 ?
    //       <span className="badge bg-success">Discharged</span> :
    //       <span className="badge bg-secondary">Admitted</span>
    //   )
    // },
    { name: 'User', selector: r => r.last_used || r.user_display || r.fname || '', width: "160px" }
  ];

  // discharged tab columns
  const dischargedColumns = [
    ...commonColumns.slice(0,3), // SL, IPD No, UHID
    { name: 'Discharge Date', selector: r => r.ipd_discharge_date || r.discharge_datetime || '', sortable: true, cell: r => formatDateTime12(r.ipd_discharge_date || r.discharge_datetime), width: "200px" },
    ...commonColumns.slice(3), // Patient Name, Bed, Referred Doctor, OT Doctor (patient name included)
    {
      name: 'IPD Bill',
      selector: r => Number(
        // prefer sum of ipd_bill_paid provided by backend; fallback to other totals
        r.ipd_bill_paid ?? r.ipd_bill_total_final ?? r.ipd_bill_total_paid ?? 0
      ).toFixed(2),
      right: true,
      width: "120px"
    },
    
    // Per-row paid breakdown columns:
    {
      name: 'Paid (Cash)',
      selector: r => Number(r.ipd_paid_cash ?? r.ipd_paid_cash ?? 0).toFixed(2),
      right: true,
      width: "110px"
    },
    {
      name: 'Paid (Card)',
      selector: r => Number(r.ipd_paid_card ?? r.ipd_paid_card ?? 0).toFixed(2),
      right: true,
      width: "110px"
    },
    {
      name: 'Paid (Online)',
      selector: r => Number(r.ipd_paid_online ?? r.ipd_paid_online ?? 0).toFixed(2),
      right: true,
      width: "110px"
    },
    
    { name: 'Due',  selector: r => Number(r.ipd_bill_total_due  ?? r.ipd_due  ?? r.due ?? 0).toFixed(2), right: true, width: "110px" },
    {
      name: 'Payment Type',
      selector: r => classifyMode(r.ipd_bill_pay_modes || r.ipd_bill_pay_mode || r.ipd_pay_mode || ''),
      width: "120px"
    },
    {
      name: 'Status',
      selector: r => r.status,
      width: "130px",
      cell: r => (
        r.status == 1 ?
          <span className="badge bg-success">Discharged</span> :
          <span className="badge bg-secondary">Admitted</span>
      )
    },
    { name: 'User', selector: r => r.last_used || r.user_display || r.fname || '', width: "160px" }
  ];

  const baseColumns = activeTab === "admitted" ? admittedColumns : dischargedColumns;
  const columns = [viewButtonColumn, ...baseColumns];

  const customStyles = {
    headCells: { style: { backgroundColor: '#f1f5fb', color:'#405189', fontSize:'15px', fontWeight:700 } },
    rows: { style: { minHeight: '56px' } },
    cells: { style: { paddingLeft: '12px', paddingRight:'12px' } }
  };

  // ---------- Render ----------
  return (
    <div className="page-content">
      <div className="container-fluid">

        {/* TAB NAV */}
        <ul className="nav nav-tabs mb-3">
          <li className="nav-item">
            <button
              className={`nav-link ${activeTab === "admitted" ? "active" : ""}`}
              onClick={() => handleTabClick("admitted")}
            >
              IPD Admitted Patient
            </button>
          </li>
          <li className="nav-item">
            <button
              className={`nav-link ${activeTab === "discharged" ? "active" : ""}`}
              onClick={() => handleTabClick("discharged")}
            >
              IPD Discharge Patient
            </button>
          </li>
        </ul>






        {/* header summary */}
        <div className="row mb-3">
          <div className="col-md-3"><div className="card p-3"><p className="mb-1 text-muted">Total Patients</p><h4>{totals.totalPatients}</h4></div></div>
          {/* For admitted tab show combined Total (Reg + Advance); for discharge show Total IPD Bill */}
          {activeTab === "admitted" ? (
            <>
            <div className="col-md-3"><div className="card p-3"><p className="mb-1 text-muted">Total (Paid)</p><h4>₹{totals.totalIpdBill}</h4></div></div>
            <div className="col-md-3"><div className="card p-3"><p className="mb-1 text-muted">Total Reg Charges</p><h4>₹{totals.totalRegCharges}</h4></div></div>
            <div className="col-md-3"><div className="card p-3"><p className="mb-1 text-muted">Total Advance</p><h4>₹{totals.totalAdvance}</h4></div></div>
            <div className="col-md-3"><div className="card p-3"><p className="mb-1 text-muted">Total Discount</p><h4>₹{totals.totalDiscount}</h4></div></div>
          </>
          ) : (
            <>
              <div className="col-md-3"><div className="card p-3"><p className="mb-1 text-muted">Total IPD Bill</p><h4>₹{totals.totalIpdBill}</h4></div></div>
              <div className="col-md-3"><div className="card p-3"><p className="mb-1 text-muted">Paid (Cash)</p><h4>₹{totals.paidCash}</h4></div></div>
              <div className="col-md-3"><div className="card p-3"><p className="mb-1 text-muted">Total Due</p><h4>₹{totals.totalDue}</h4></div></div>
            </>
          )}
        </div>

        {/* filters */}
        <div className="card mb-3">
          <div className="card-body">
            <div className="row g-2">
              <div className="col-md-3">
                <label>Search</label>
                <input name="search" value={filters.search} onChange={handleFilterChange} className="form-control" placeholder="UHID / Name / Mobile"/>
              </div>
              {activeTab === 'discharged' ? (
  <>
  <div className="col-md-2">
    <label>Discharge From</label>
    <input
      type="date"
      name="dischargeFrom"
      value={filters.dischargeFrom}
      onChange={handleFilterChange}
      className="form-control"
    />
  </div>

  <div className="col-md-1">
    <label>Time</label>
    <input
      type="time"
      name="dischargeFromTime"
      value={filters.dischargeFromTime}
      onChange={handleFilterChange}
      className="form-control"
    />
    <small className="text-muted d-block text-center">
      {formatTime12(filters.dischargeFromTime)}
    </small>
  </div>

  <div className="col-md-2">
    <label>Discharge To</label>
    <input
      type="date"
      name="dischargeTo"
      value={filters.dischargeTo}
      onChange={handleFilterChange}
      className="form-control"
    />
  </div>

  <div className="col-md-1">
    <label>Time</label>
    <input
      type="time"
      name="dischargeToTime"
      value={filters.dischargeToTime}
      onChange={handleFilterChange}
      className="form-control"
    />
    <small className="text-muted d-block text-center">
      {formatTime12(filters.dischargeToTime)}
    </small>
  </div>
</>

) : (
  // existing admission from/to inputs (leave as-is)
  <>
   {/* Admission From/To (admitted tab) */}
<div className="col-md-2">
  <label>From (Date)</label>
  <input type="date" name="from" value={filters.from} onChange={handleFilterChange} className="form-control" />
</div>
<div className="col-md-1">
  <label>Time</label>
  <input type="time" name="fromTime" value={filters.fromTime} onChange={handleFilterChange} className="form-control" />
  <small className="text-muted d-block">{formatTime12(filters.fromTime)}</small>
</div>

<div className="col-md-2">
  <label>To (Date)</label>
  <input type="date" name="to" value={filters.to} onChange={handleFilterChange} className="form-control" />
</div>
<div className="col-md-1">
  <label>Time</label>
  <input type="time" name="toTime" value={filters.toTime} onChange={handleFilterChange} className="form-control" />
  <small className="text-muted d-block">{formatTime12(filters.toTime)}</small>
</div>

  </>
)}


<div className="col-md-3">
  <label>Reffered Doctor</label>
  <Select
    isClearable
    value={docsFromServer.find(d => String(d.user_id) === String(filters.doctorId)) ? {
      value: String(filters.doctorId),
      label: (docsFromServer.find(d => String(d.user_id) === String(filters.doctorId)).fname || docsFromServer.find(d => String(d.user_id) === String(filters.doctorId)).name || docsFromServer.find(d => String(d.user_id) === String(filters.doctorId)).username)
    } : null}
    onChange={(sel) => {
      if (!sel) {
        setFilters(prev => ({ ...prev, doctorId: "", doctorSearch: "" }));
      } else {
        setFilters(prev => ({ ...prev, doctorId: String(sel.value), doctorSearch: String(sel.label) }));
      }
    }}
    options={docsFromServer.map(d => ({
      value: String(d.user_id),
      label: d.fname || d.name || d.username || String(d.user_id)
    }))}
    placeholder="Select doctor..."
  />
  <small className="text-muted">Select a doctor to filter</small>
</div>

            </div>

            <div className="row g-2 mt-3">
              <div className="col-md-3">
                <label>User</label>
                <select name="userId" value={filters.userId} onChange={handleFilterChange} className="form-select">
                  <option value="">All Users</option>
                  {users.map(u => <option key={u.user_id} value={u.user_id}>{u.name}</option>)}
                </select>
              </div>

              <div className="col-md-2">
                <label>Payment</label>
                <select name="paymentType" value={filters.paymentType} onChange={handleFilterChange} className="form-select">
                  <option value="All">All</option>
                  <option value="Cash">Cash</option>
                  <option value="Card">Card</option>
                  <option value="Online">Online</option>
                </select>
              </div>
            </div>
          </div>
        </div>

        {/* table */}
        <div className="card">
          <div className="card-body">

{/* --- Export button (moved above table) --- */}
<div className="d-flex justify-content-end mb-2">
  <button
    className="btn btn-outline-primary"
    onClick={async () => {
      await handleExportPdf(); // call the new improved export function
    }}
  >
    Export PDF
  </button>
</div>


            <DataTable
              columns={columns}
              data={filteredRecords}
              customStyles={customStyles}
              pagination
              highlightOnHover
              persistTableHead
              responsive
              onRowClicked={async (row) => {
                // if user clicks on Discharged tab we try to fetch and show services
                if (activeTab !== 'discharged') {
                  openPatientCard(row);
                  return;
                }
              
                try {
                  const fd = new FormData();
                  if (row.ipd_id) fd.append('ipd_id', String(row.ipd_id));
                  if (row.ipd_uhid) fd.append('ipd_uhid', String(row.ipd_uhid));
                  if (row.ipd_no) fd.append('ipd_no', String(row.ipd_no));
              
                  const itemsUrl = AppConst.BASE_URL + (AppConst.getIpdBillItemsUrl || '/api/get_ipd_bill_items');
              
                  // debug: show the request being sent
                  //console.debug('Fetching bill items for', { ipd_id: row.ipd_id, ipd_uhid: row.ipd_uhid, ipd_no: row.ipd_no, itemsUrl });
              
                  const res = await axios.post(itemsUrl, fd);
              
                  // debug response
                  console.debug('get_ipd_bill_items response', res?.data);
              
                  // expected: res.data.status === true and res.data.items is array
                  if (res?.data?.status && Array.isArray(res.data.items)) {
                    const items = res.data.items;
              
                    // Build DOM-safe HTML instead of concatenating possibly problematic strings
                    const listEl = document.createElement('ul');
                    listEl.style.textAlign = 'left';
                    listEl.style.paddingLeft = '18px';
                    listEl.style.margin = '0';
              
                    if (items.length === 0) {
                      const li = document.createElement('li');
                      li.textContent = 'No services found';
                      listEl.appendChild(li);
                    } else {
                      items.forEach(it => {
                        const li = document.createElement('li');
                        const svc = it.ipd_bill_item_service ?? it.service ?? it.name ?? 'Unknown service';
                        const price = Number(it.ipd_bill_item_price ?? it.price ?? 0).toFixed(2);
                        li.innerHTML = `${svc} <small class="text-muted">(${price})</small>`;
                        listEl.appendChild(li);
                      });
                    }
              
                    // Use Swal with a DOM node (SweetAlert2 accepts a DOM node as `html` value)
                    Swal.fire({
                      title: `Services used — ${row.ipd_patient_name || ''}`,
                      html: listEl,
                      width: 700,
                      showCloseButton: true,
                      focusConfirm: false,
                      confirmButtonText: 'Close'
                    });
                  } else {
                    // If server returned no items (or structure differs), show patient card as fallback
                    console.warn('Unexpected items response shape; falling back to patient card', res?.data);
                    openPatientCard(row);
                  }
                } catch (err) {
                  console.error('Error fetching ipd bill items', err);
                  Swal.fire('Error', 'Could not load services. Please check console / network.', 'error');
                  openPatientCard(row);
                }
              }}
              

            />

            {/* footer summary — differs by tab */}
            {activeTab === "admitted" ? (
  <div className="text-end mt-3 text-muted">
    <strong>Total (Reg + Advance):</strong> ₹{totals.totalAmount} &nbsp;|&nbsp;
    <strong>Total Reg Charges:</strong> ₹{totals.totalRegCharges} &nbsp;|&nbsp;
    <strong>Total Advance:</strong> ₹{totals.totalAdvance} &nbsp;|&nbsp;
    <span style={{marginLeft:8}}>💵 <strong>Paid (Cash):</strong> ₹{totals.paidCash}</span> &nbsp;|&nbsp;
    <span style={{marginLeft:8}}>💳 <strong>Paid (Card):</strong> ₹{totals.paidCard}</span> &nbsp;|&nbsp;
    <span style={{marginLeft:8}}>🌐 <strong>Paid (Online):</strong> ₹{totals.paidOnline}</span> &nbsp;|&nbsp;
    <strong>Total Due:</strong> ₹{totals.totalDue} &nbsp;|&nbsp;
    <strong>Total Patients:</strong> {totals.totalPatients}
  </div>
) : (
  <div className="text-end mt-3 text-muted">
    <strong>Total IPD Bill:</strong> ₹{totals.totalIpdBill} &nbsp;|&nbsp;
    <span style={{marginLeft:8}}>💵 <strong>Paid (Cash):</strong> ₹{totals.paidCash}</span> &nbsp;|&nbsp;
    <span style={{marginLeft:8}}>💳 <strong>Paid (Card):</strong> ₹{totals.paidCard}</span> &nbsp;|&nbsp;
    <span style={{marginLeft:8}}>🌐 <strong>Paid (Online):</strong> ₹{totals.paidOnline}</span> &nbsp;|&nbsp;
    <strong>Total Due:</strong> ₹{totals.totalDue} &nbsp;|&nbsp;
    <strong>Total Patients:</strong> {totals.totalPatients}
  </div>
)}


          </div>
        </div>

      </div>
    </div>
  );
}
